import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { HugeiconsIcon } from '@hugeicons/react';
import { Clock01Icon, Alert02Icon, ArrowRight01Icon } from '@hugeicons/core-free-icons';
import { getTrialState, getTrialChipCopy, getTrialHoursLeft, getTrialDaysLeft } from '@overlayqa/shared';
import { useBillingInfo } from '../hooks/useBillingInfo';
import { capture } from '../lib/analytics';
import { log } from '@/lib/logger';

/**
 * Compact trial-runway chip for the header row.
 *
 * Emily, 2026-08-31: "lets have it smaller width on the same row as
 * notifications and avatar not a full dismissable banner", then "it should be
 * before the bell so it has space". So it mounts inside Layout's existing
 * header ahead of NotificationBell, with no dismiss affordance. On phones
 * Layout gives it a separate row so account controls remain reachable.
 *
 * WHAT THIS DOES NOT COVER, deliberately. `getTrialChipCopy` returns null for
 * `expired`, so that state stays with the full-width TrialNudgeBanner. Expiry
 * is not a nudge: plan goes null and every write 403s with TRIAL_EXPIRED
 * (middleware/planLimits.ts:309), so the workspace is read-only. A corner chip
 * would announce that in the one place a user is least likely to look, and
 * they would learn it instead from a save that silently refuses. The same
 * applies to the non-owner read-only note the banner renders.
 *
 * The whole chip is ONE link, carrying a visible CTA inside it (Emily: "add a
 * see plans links"). Not a nested <a>: that is invalid markup, browsers and
 * screen readers resolve it inconsistently, and it would add a second focus
 * stop for a destination the chip already goes to. The CTA wording comes from
 * getTrialNudgeCopy, so it sharpens from "See plans" to "Pick a plan" on the
 * same schedule as the expired bar and cannot drift from it. Not "Upgrade": the
 * chip lands on /billing, which offers Keep / Switch / Contact sales and no
 * control called Upgrade (2026-09-16).
 */
export function TrialStatusChip({ className = '' }: { className?: string }) {
  const { data: info } = useBillingInfo();
  const location = useLocation();
  const [hasFiredView, setHasFiredView] = useState(false);

  // Render nothing until billing resolves. The chip sits in a flex row beside
  // the bell, so appearing late would shift both controls left-to-right on
  // first paint; an absent chip simply never reserves the space.
  const trialState = info
    ? getTrialState({
        plan: info.plan,
        betaExpiresAt: info.betaExpiresAt,
        isOwner: info.isOwner,
        trialTimeZone: info.trialTimeZone,
      })
    : 'paid';

  const hoursLeft = getTrialHoursLeft(info?.betaExpiresAt ?? null);
  const chipCopy = getTrialChipCopy(trialState, hoursLeft, info?.issuesCaptured,
    getTrialDaysLeft(info?.betaExpiresAt ?? null, info?.trialTimeZone));

  // Stand down on /billing. Its "See plans" would link to the page the user is
  // already reading, and the Current Plan card there states the trial and its
  // days left in more detail than a chip can. The full-width banner suppressed
  // itself here for the same reason; the rule did not survive the move to the
  // header until a live billing-page frame showed the chip sitting above a card
  // repeating it, CTA pointing at itself.
  const isOnBillingPage = location.pathname.startsWith('/billing');
  const copy = isOnBillingPage ? null : chipCopy;

  useEffect(() => {
    if (!copy || hasFiredView) return;
    setHasFiredView(true);
    // `surface` keeps this splittable from the full-width banner's own events.
    // Without it the countdown's move from bar to chip would look like a change
    // in trial volume rather than a change in where the nudge lives.
    capture('trial_nudge_shown', {
      state: trialState,
      hours_left: Math.round(hoursLeft),
      surface: 'header_chip',
    });
    log.info('[TrialStatusChip] shown', { state: trialState });
  }, [copy, hasFiredView, trialState, hoursLeft]);

  const handleClick = useCallback(() => {
    capture('trial_nudge_cta_clicked', { state: trialState, surface: 'header_chip' });
  }, [trialState]);

  if (!copy) return null;

  const VARIANT_CLASSES: Record<string, string> = {
    info: 'bg-[var(--color-trial-chip-info-bg)] text-[var(--color-trial-chip-info-text)]',
    warning: 'bg-[var(--color-trial-chip-warning-bg)] text-[var(--color-trial-chip-warning-text)]',
    urgent: 'bg-[var(--color-trial-chip-urgent-bg)] text-[var(--color-trial-chip-urgent-text)]',
  };

  return (
    <Link
      to="/billing"
      onClick={handleClick}
      title={copy.title}
      aria-label={copy.title}
      className={`group inline-flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2 text-xs font-semibold transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-primary)] focus-visible:ring-offset-2 ${VARIANT_CLASSES[copy.variant]} ${className}`}
    >
      <HugeiconsIcon
        icon={copy.variant === 'info' ? Clock01Icon : Alert02Icon}
        size={14}
        strokeWidth={2}
        color="currentColor"
        className="shrink-0"
        aria-hidden="true"
      />
      <span className="text-cap-trim">{copy.label}</span>
      {/* Visible CTA inside the same link, never a nested one: the whole chip
          is already the target, and an <a> inside an <a> is invalid markup
          that screen readers and browsers resolve inconsistently. The rule
          carries the affordance instead of a second focus stop. */}
      <span
        className="ml-1 border-l border-current/25 pl-2 font-semibold underline-offset-2 group-hover:underline"
        aria-hidden="true"
      >
        {copy.cta}
      </span>
      <HugeiconsIcon
        icon={ArrowRight01Icon}
        size={12}
        strokeWidth={2}
        color="currentColor"
        className="shrink-0 -ml-1"
        aria-hidden="true"
      />
    </Link>
  );
}
